import React from 'react';
import { Rect, Text, Image as KonvaImage, Group, Line } from 'react-konva';
import {
  LabelElement,
  isTextElement,
  isShapeElement,
  isQRCodeElement,
  isImageElement,
  isBarcodeElement,
  isLineElement,
} from '../../types';
import { SalesOrder } from 'services/salesOrders';
import {
  MONEY_FIELDS,
  formatCurrency,
  getResolvedDisplayValue,
  getSOValueAtPath,
} from '../../helpers';

// Helper function to format text for elements
const getFormattedElementText = (
  element: LabelElement,
  previewMode: boolean,
  previewData: SalesOrder | null
): string => {
  const fieldKey = element.dynamicField;
  const isMoneyField = fieldKey && MONEY_FIELDS.includes(fieldKey);
  if (previewMode && fieldKey && previewData && isMoneyField) {
    const rawValue = getSOValueAtPath(previewData, fieldKey);
    return formatCurrency(rawValue);
  }
  // Fallback to getResolvedDisplayValue for other cases or if not a money field in preview
  return getResolvedDisplayValue(element, previewMode, previewData);
};

interface RenderedElementContentProps {
  element: LabelElement;
  commonProps: any; // Common Konva props like x, y, id, key, draggable, event handlers
  previewMode?: boolean;
  previewData: SalesOrder | null;
  konvaQrImage?: HTMLImageElement | null;
  image?: HTMLImageElement | null;
  konvaBarcodeImage?: HTMLImageElement | null;
}

const RenderedElementContent: React.FC<RenderedElementContentProps> = ({
  element,
  commonProps,
  previewMode,
  previewData,
  konvaQrImage,
  image,
  konvaBarcodeImage,
}) => {
  switch (element.type) {
    case 'text':
      if (isTextElement(element)) {
        return (
          <Group {...commonProps}>
            {element.backgroundColor && (
              <Rect
                width={element.width}
                height={element.height}
                fill={element.backgroundColor}
              />
            )}
            <Text
              width={element.width}
              height={element.height}
              text={getFormattedElementText(
                element,
                previewMode ?? false,
                previewData
              )}
              fontSize={element.fontSize || 16}
              fontFamily={element.fontFamily || 'Arial'}
              fill={element.fill || '#000000'}
              align={element.align || 'left'}
              verticalAlign="middle"
            />
          </Group>
        );
      }
      return null;

    case 'box':
      if (isShapeElement(element)) {
        return (
          <Rect
            {...commonProps}
            width={element.width} // Ensure width and height from element are used, commonProps might override if not careful
            height={element.height}
            fill={element.fill || 'transparent'}
            stroke={element.stroke || '#000000'}
            strokeWidth={element.lineWidth || 1}
          />
        );
      }
      return null;

    case 'qrcode':
      if (isQRCodeElement(element)) {
        const size = Math.min(element.width, element.height);
        return (
          <Group {...commonProps}>
            {konvaQrImage && (
              <KonvaImage width={size} height={size} image={konvaQrImage} />
            )}
          </Group>
        );
      }
      return null;

    case 'image':
      if (isImageElement(element)) {
        return (
          <KonvaImage
            {...commonProps}
            width={element.width} // Ensure width and height from element are used
            height={element.height}
            image={image || undefined}
          />
        );
      }
      return null;

    case 'barcode':
      if (isBarcodeElement(element) && commonProps) {
        const textHeight = Math.min(30, element.height * 0.25);
        const fontSize = Math.max(12, textHeight * 0.6);
        return (
          <Group {...commonProps}>
            {konvaBarcodeImage && (
              <KonvaImage
                width={element.width}
                height={element.height - textHeight}
                image={konvaBarcodeImage}
              />
            )}
            <Text
              x={0}
              y={element.height - textHeight}
              width={element.width}
              height={textHeight}
              text={getResolvedDisplayValue(
                element,
                previewMode ?? false,
                previewData
              )}
              fontSize={fontSize}
              fontFamily={element.fontFamily || 'Arial'} // Added fontFamily for consistency
              align="center"
              verticalAlign="middle"
              fill={element.fill || '#000'} // Added fill for consistency
            />
          </Group>
        );
      }
      return null;

    case 'line':
      if (isLineElement(element)) {
        return (
          <Group {...commonProps}>
            <Rect
              width={element.width}
              height={20}
              y={-10}
              fill="transparent"
            />
            <Line
              points={[0, 0, element.width, 0]}
              stroke={element.stroke || '#000000'}
              strokeWidth={element.lineWidth || 1}
            />
          </Group>
        );
      }
      return null;

    default:
      return null;
  }
};

export default RenderedElementContent;
